<template>
  <div class="smart-search-test">
    <!-- Header -->
    <div class="header">
      <div class="header-content">
        <h1>Smart Paper Search</h1>
        <p class="subtitle">AI-powered academic paper discovery</p>
      </div>
    </div>

    <!-- Main Chat Container -->
    <div class="chat-container">
      <!-- Messages Area -->
      <div class="messages-container" ref="messagesContainer">
        <!-- Welcome Message -->
        <div v-if="!hasAnyMessages" class="welcome-message">
          <div class="welcome-icon">📚</div>
          <h2>How can I help you find papers today?</h2>
          <p>Ask me anything about academic research, like:</p>
          <div class="suggestion-chips">
            <el-button
              v-for="suggestion in suggestions"
              :key="suggestion"
              size="small"
              round
              @click="useSuggestion(suggestion)"
            >
              {{ suggestion }}
            </el-button>
          </div>
        </div>

        <!-- Conversation Messages -->
        <div
          v-for="(message, index) in messages"
          :key="'msg-' + index"
          :class="['message-wrapper', message.type]"
        >
          <!-- User Message -->
          <div v-if="message.type === 'user'" class="message user-message">
            <div class="message-content">
              <div class="message-text">{{ message.content }}</div>
            </div>
            <div class="message-avatar user-avatar">
              <span>👤</span>
            </div>
          </div>

          <!-- Stage Message (每个stage单独对话) -->
          <div
            v-if="message.type === 'stage'"
            class="message ai-message stage-message"
          >
            <div class="message-avatar ai-avatar">
              <span>{{ message.icon }}</span>
            </div>
            <div class="message-content">
              <div class="message-header">
                <span class="message-time">{{ message.timestamp }}</span>
                <el-button
                  type="text"
                  size="mini"
                  @click="toggleStageDetails(message.id)"
                >
                  {{ message.showDetails ? "Hide" : "Show" }}
                  {{ message.updates.length }} updates
                  <i
                    :class="
                      message.showDetails
                        ? 'el-icon-arrow-up'
                        : 'el-icon-arrow-down'
                    "
                  ></i>
                </el-button>
              </div>
              <div class="stage-title">
                {{ getStageDisplayName(message.stage) }}
                <el-tag
                  v-if="message.isCompleted"
                  size="mini"
                  type="success"
                  effect="plain"
                  style="margin-left: 8px"
                  >Completed</el-tag
                >
              </div>

              <!-- Collapsible Updates -->
              <el-collapse-transition>
                <div v-show="message.showDetails" class="stage-updates">
                  <div
                    v-for="(update, idx) in message.updates"
                    :key="'update-' + idx"
                    class="update-item"
                  >
                    <div class="update-content">{{ update.message }}</div>
                    <div v-if="update.details" class="update-meta">
                      <el-tag v-if="update.details.year" size="mini" type="info"
                        >Year: {{ update.details.year }}</el-tag
                      >
                      <el-tag
                        v-if="update.details.count"
                        size="mini"
                        type="success"
                        >Count: {{ update.details.count }}</el-tag
                      >
                    </div>
                  </div>
                </div>
              </el-collapse-transition>
            </div>
          </div>

          <!-- Results Message -->
          <div v-if="message.type === 'results'" class="message ai-message">
            <div class="message-avatar ai-avatar">
              <span>📚</span>
            </div>
            <div class="message-content">
              <div class="message-header">
                <span class="message-time">{{ message.timestamp }}</span>
              </div>
              <div class="message-text">
                ✅ Found {{ message.results.length }} relevant papers
              </div>

              <!-- Results Table -->
              <div class="results-table-container">
                <el-table
                  :data="message.results"
                  style="width: 100%"
                  :max-height="400"
                  size="small"
                  stripe
                  class="results-table"
                >
                  <el-table-column prop="title" label="Title" min-width="250">
                    <template slot-scope="{ row }">
                      <a
                        :href="row.url"
                        target="_blank"
                        class="table-title-link"
                      >
                        {{ row.title }}
                      </a>
                    </template>
                  </el-table-column>
                  <el-table-column prop="author" label="Authors" width="200">
                    <template slot-scope="{ row }">
                      <el-tooltip
                        :content="formatFullAuthors(row.author)"
                        placement="top"
                        :disabled="!isAuthorsLong(row.author)"
                      >
                        <span class="authors-cell">{{
                          formatAuthors(row.author)
                        }}</span>
                      </el-tooltip>
                    </template>
                  </el-table-column>
                  <el-table-column
                    prop="year"
                    label="Year"
                    width="80"
                    align="center"
                  ></el-table-column>
                  <el-table-column prop="journal" label="Venue" width="150">
                    <template slot-scope="{ row }">
                      <span class="venue-cell">{{
                        row.journal || row.conference || "N/A"
                      }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    prop="citations"
                    label="Citations"
                    width="100"
                    align="center"
                  >
                    <template slot-scope="{ row }">
                      <el-tag size="mini" type="warning">
                        {{ row.citations || row.kqi || 0 }}
                      </el-tag>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="message.type === 'error'" class="message ai-message">
            <div class="message-avatar ai-avatar">
              <span>⚠️</span>
            </div>
            <div class="message-content error-content">
              <div class="message-header">
                <span class="message-time">{{ message.timestamp }}</span>
              </div>
              <div class="message-text">{{ message.content }}</div>
            </div>
          </div>
        </div>

        <!-- Loading Indicator -->
        <div v-if="isSearching" class="message ai-message loading-message">
          <div class="message-avatar ai-avatar">
            <span>🤖</span>
          </div>
          <div class="message-content">
            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="input-area">
        <div class="input-container">
          <el-input
            v-model="searchParams.query"
            type="textarea"
            :rows="1"
            :autosize="{ minRows: 1, maxRows: 6 }"
            placeholder="Ask about any research topic..."
            class="chat-input"
            resize="none"
            @keyup.enter.native="handleEnter"
            @focus="onInputFocus"
          />
          <div class="input-actions">
            <el-button
              type="primary"
              circle
              class="send-button"
              :disabled="!searchParams.query.trim() || isSearching"
              @click="startSearch"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 11L12 6L17 11M12 18V7"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </el-button>
          </div>
        </div>

        <!-- Advanced Options Toggle -->
        <div class="advanced-toggle">
          <el-button
            type="text"
            size="small"
            @click="showAdvanced = !showAdvanced"
          >
            {{ showAdvanced ? "Hide" : "Show" }} Advanced Options
            <i
              :class="showAdvanced ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
            ></i>
          </el-button>
        </div>

        <!-- Advanced Options Panel -->
        <el-collapse-transition>
          <div v-show="showAdvanced" class="advanced-panel">
            <el-form
              :model="searchParams"
              label-width="90px"
              size="mini"
              class="compact-form"
            >
              <div class="advanced-grid">
                <el-form-item label="Start Year">
                  <el-input-number
                    v-model="searchParams.start_year"
                    :min="2000"
                    :max="2026"
                    controls-position="right"
                    style="width: 100%"
                  />
                </el-form-item>
                <el-form-item label="End Year">
                  <el-input-number
                    v-model="searchParams.end_year"
                    :min="2000"
                    :max="2026"
                    controls-position="right"
                    style="width: 100%"
                  />
                </el-form-item>
                <el-form-item label="Limit/Year">
                  <el-input-number
                    v-model="searchParams.limit_per_year"
                    :min="1"
                    :max="100"
                    controls-position="right"
                    style="width: 100%"
                  />
                </el-form-item>
                <el-form-item label="Top K">
                  <el-input-number
                    v-model="searchParams.top_k"
                    :min="1"
                    :max="100"
                    controls-position="right"
                    style="width: 100%"
                  />
                </el-form-item>
              </div>
            </el-form>
          </div>
        </el-collapse-transition>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SmartSearchTest",
  data() {
    return {
      searchParams: {
        query: "",
        start_year: 2000,
        end_year: 2026,
        limit_per_year: 20,
        top_k: 50,
      },
      isSearching: false,
      messages: [],
      eventSource: null,
      showAdvanced: false,
      suggestions: [
        "data center networking",
        "machine learning algorithms",
        "distributed systems",
        "computer vision",
        "natural language processing",
      ],
      stageIcons: {
        initial_search: "🔍",
        intent_analysis: "🧠",
        deep_search: "🔬",
        completed: "✅",
        error: "❌",
      },
      activeStages: {},
    };
  },
  computed: {
    hasAnyMessages() {
      return this.messages.length > 0;
    },
  },
  methods: {
    handleEnter(event) {
      if (!event.shiftKey && this.searchParams.query.trim()) {
        event.preventDefault();
        this.startSearch();
      }
    },

    onInputFocus() {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    useSuggestion(suggestion) {
      this.searchParams.query = suggestion;
      this.$nextTick(() => {
        const input = this.$el.querySelector(".chat-input textarea");
        if (input) input.focus();
      });
    },

    startSearch() {
      if (!this.searchParams.query.trim() || this.isSearching) return;

      this.isSearching = true;
      this.activeStages = {};

      // Add user message
      const userMessage = {
        type: "user",
        content: this.searchParams.query,
        timestamp: new Date().toLocaleTimeString(),
      };
      this.messages.push(userMessage);

      const query = this.searchParams.query;
      this.searchParams.query = "";

      // Build API URL
      const baseUrl = process.env.VUE_APP_BASE_API || "";
      const params = new URLSearchParams();
      params.append("query", query);
      params.append("start_year", this.searchParams.start_year);
      params.append("end_year", this.searchParams.end_year);
      params.append("limit_per_year", this.searchParams.limit_per_year);
      params.append("top_k", this.searchParams.top_k);

      const url = `${baseUrl}/v1/smart_paper_search?${params.toString()}`;

      this.eventSource = new EventSource(url);

      this.eventSource.onmessage = (event) => {
        console.log("Received SSE event:", event.data);
        try {
          const data = JSON.parse(event.data);
          const timestamp = new Date().toLocaleTimeString();
          const stage = data.stage;

          if (
            stage === "initial_search" ||
            stage === "intent_analysis" ||
            stage === "deep_search"
          ) {
            if (!this.activeStages[stage]) {
              const stageMessage = {
                id: stage + "-" + Date.now(),
                type: "stage",
                stage: stage,
                icon: this.stageIcons[stage] || "📋",
                updates: [],
                timestamp: timestamp,
                showDetails: true,
                isCompleted: false,
              };
              this.messages.push(stageMessage);
              this.activeStages[stage] = stageMessage.id;
            }

            const stageMsg = this.messages.find(
              (msg) => msg.id === this.activeStages[stage]
            );
            if (stageMsg) {
              stageMsg.updates.push({
                message: data.message,
                details: {
                  year: data.year,
                  count: data.count,
                },
                timestamp: timestamp,
              });
              this.scrollToBottom();
            }
          } else if (stage === "completed") {
            // 标记所有stage为完成并自动折叠
            Object.values(this.activeStages).forEach((stageId) => {
              const stageMsg = this.messages.find((msg) => msg.id === stageId);
              if (stageMsg) {
                stageMsg.isCompleted = true;
                stageMsg.showDetails = false; // 自动折叠
              }
            });

            if (data.data) {
              const results = (data.data.top_papers || []).map((paper) => ({
                title: paper.title,
                author: paper.author || [],
                year: paper.year,
                journal: paper.journal || paper.conference || "",
                citations: paper.kqi || paper.citations || 0,
                url: paper.url ? paper.url.replace(/[` ]/g, "") : "",
              }));

              this.addResultsMessage(results, timestamp);
            }
            this.finalizeSearch();
          } else if (stage === "error") {
            this.addErrorMessage(data.message, timestamp);
            this.finalizeSearch();
          }
        } catch (error) {
          console.error("Failed to parse SSE data:", error);
          this.addErrorMessage(
            "Failed to parse server response",
            new Date().toLocaleTimeString()
          );
          this.finalizeSearch();
        }
      };

      this.eventSource.onerror = (error) => {
        console.error("SSE connection error:", error);
        this.addErrorMessage(
          "Server connection failed",
          new Date().toLocaleTimeString()
        );
        this.finalizeSearch();
      };

      this.scrollToBottom();
    },

    getStageDisplayName(stage) {
      const names = {
        initial_search: "Initial Search",
        intent_analysis: "Intent Analysis",
        deep_search: "Deep Search",
      };
      return names[stage] || stage;
    },

    toggleStageDetails(stageId) {
      const stageMsg = this.messages.find((msg) => msg.id === stageId);
      if (stageMsg) {
        stageMsg.showDetails = !stageMsg.showDetails;
      }
    },

    addResultsMessage(results, timestamp) {
      this.messages.push({
        type: "results",
        results,
        timestamp,
      });
      this.scrollToBottom();
    },

    addErrorMessage(content, timestamp) {
      this.messages.push({
        type: "error",
        content,
        timestamp,
      });
      this.scrollToBottom();
    },

    finalizeSearch() {
      this.isSearching = false;
      this.activeStages = {};
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
    },

    cancelSearch() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
      this.isSearching = false;
      this.activeStages = {};
      this.addErrorMessage("Search cancelled", new Date().toLocaleTimeString());
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    formatAuthors(authors) {
      if (!authors || !Array.isArray(authors) || authors.length === 0)
        return "N/A";
      const maxAuthors = 3;
      if (authors.length <= maxAuthors) {
        return authors.join(", ");
      }
      return `${authors.slice(0, maxAuthors).join(", ")} +${
        authors.length - maxAuthors
      } more`;
    },

    formatFullAuthors(authors) {
      if (!authors || !Array.isArray(authors)) return "N/A";
      return authors.join(", ");
    },

    isAuthorsLong(authors) {
      if (!authors || !Array.isArray(authors)) return false;
      return authors.length > 3 || authors.join(", ").length > 50;
    },
  },
  beforeDestroy() {
    if (this.eventSource) {
      this.eventSource.close();
    }
  },
};
</script>

<style scoped>
.smart-search-test {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: white;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial,
    sans-serif;
}

/* Header */
.header {
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
  color: #333;
  padding: 20px;
  text-align: center;
  flex-shrink: 0;
}

.header-content h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-content .subtitle {
  margin: 5px 0 0 0;
  font-size: 0.9rem;
  color: #666;
}

/* Chat Container */
.chat-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  border: 1px solid #e8e8e8;
  border-top: none;
  overflow: hidden;
}

/* Messages Container */
.messages-container {
  flex: 1;
  padding: 30px;
  overflow-y: auto;
  background: white;
  scroll-behavior: smooth;
}

.messages-container::-webkit-scrollbar {
  width: 8px;
}

.messages-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.messages-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Welcome Message */
.welcome-message {
  text-align: center;
  padding: 60px 20px;
  color: #666;
}

.welcome-icon {
  font-size: 3rem;
  margin-bottom: 20px;
}

.welcome-message h2 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 1.8rem;
}

.welcome-message p {
  margin: 0 0 20px 0;
  color: #666;
}

.suggestion-chips {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 20px;
}

/* Message Styles */
.message-wrapper {
  margin-bottom: 20px;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message {
  display: flex;
  gap: 15px;
  max-width: 90%;
}

.user-message {
  margin-left: auto;
  flex-direction: row-reverse;
}

.ai-message {
  margin-right: auto;
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.user-avatar {
  background: linear-gradient(135deg, #10a37f, #0d8c6e);
  color: white;
}

.ai-avatar {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.message-content {
  flex: 1;
  padding: 12px 16px;
  border-radius: 18px;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  min-width: 0; /* 防止flex item溢出 */
}

.user-message .message-content {
  background: linear-gradient(135deg, #10a37f, #0d8c6e);
  color: white;
  border-bottom-right-radius: 4px;
}

.ai-message .message-content {
  background: #f8f9fa;
  color: #333;
  border-bottom-left-radius: 4px;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.message-time {
  font-size: 0.7rem;
  color: #999;
}

.user-message .message-time {
  color: rgba(255, 255, 255, 0.7);
}

.message-text {
  font-size: 0.95rem;
  line-height: 1.5;
  white-space: pre-line;
  text-align: left; /* 左对齐 */
}

/* Stage Message */
.stage-message .message-content {
  background: #fff;
  border: 1px solid #e8e8e8;
}

.stage-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.stage-updates {
  margin-top: 10px;
  border-top: 1px solid #e8e8e8;
  padding-top: 10px;
}

.update-item {
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.update-item:last-child {
  border-bottom: none;
}

.update-content {
  font-size: 0.85rem;
  color: #666;
  text-align: left; /* 左对齐 */
}

.update-meta {
  margin-top: 4px;
}

/* Results Table */
.results-table-container {
  margin-top: 15px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
}

.results-table {
  text-align: left; /* 表格左对齐 */
}

.results-table :deep(.el-table__header th) {
  background: #f5f7fa;
  color: #333;
  font-weight: 600;
  font-size: 0.85rem;
  text-align: left !important;
}

.results-table :deep(.el-table__cell) {
  text-align: left !important;
}

.table-title-link {
  color: #1890ff;
  text-decoration: none;
  font-weight: 500;
}

.table-title-link:hover {
  text-decoration: underline;
}

.authors-cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  text-align: left;
}

.venue-cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

/* Error Message */
.error-content {
  background: #fff1f0 !important;
  color: #cf1322 !important;
  border: 1px solid #ffccc7;
}

/* Loading Message */
.loading-message .message-content {
  background: white !important;
  box-shadow: none !important;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 10px 0;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #666;
  animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%,
  60%,
  100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
}

/* Input Area */
.input-area {
  background: white;
  border-top: 1px solid #e8e8e8;
  padding: 20px;
  flex-shrink: 0;
}

.input-container {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  max-width: 900px;
  margin: 0 auto;
}

.chat-input {
  flex: 1;
  background: #f9fafb;
  border: 1px solid #e8e8e8;
  border-radius: 25px;
  transition: all 0.3s ease;
}

.chat-input:hover {
  background: #f0f0f0;
}

.chat-input:focus-within {
  background: white;
  border-color: #10a37f;
  box-shadow: 0 0 0 3px rgba(16, 163, 127, 0.1);
}

.chat-input :deep(textarea) {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  padding: 12px 16px !important;
  font-size: 0.95rem;
  text-align: left;
}

.chat-input :deep(textarea:focus) {
  border: none !important;
  box-shadow: none !important;
}

.send-button {
  width: 44px;
  height: 44px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.send-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.send-button svg {
  width: 20px;
  height: 20px;
}

/* Advanced Options */
.advanced-toggle {
  text-align: center;
  margin-top: 10px;
}

.advanced-toggle .el-button {
  color: #666;
}

.advanced-panel {
  margin-top: 15px;
  padding: 15px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
}

.advanced-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.compact-form :deep(.el-form-item) {
  margin-bottom: 0;
}

.compact-form :deep(.el-form-item__label) {
  font-size: 0.8rem;
  padding-right: 8px;
  line-height: 28px;
}

.compact-form :deep(.el-form-item__content) {
  line-height: 28px;
}

.compact-form :deep(.el-input-number) {
  width: 100%;
}

.compact-form :deep(.el-input-number__decrease),
.compact-form :deep(.el-input-number__increase) {
  width: 22px;
  font-size: 0.8rem;
}

.compact-form :deep(.el-input__inner) {
  height: 28px;
  line-height: 28px;
  font-size: 0.8rem;
  padding-left: 8px;
  padding-right: 22px;
}

/* Responsive */
@media (max-width: 768px) {
  .chat-container {
    max-width: 100%;
  }

  .messages-container {
    padding: 20px 15px;
  }

  .message {
    max-width: 95%;
  }

  .advanced-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .advanced-grid {
    grid-template-columns: 1fr;
  }
}
</style>
